
import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const EfficiencySection = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const metrics = [
    {
      percentage: "80%",
      title: "Faster Threat Response",
      description: "Reduce the time to detect and respond to threats by leveraging AI automation.",
      details: "Our AI-driven platform automatically identifies and responds to threats in real-time, reducing the average time to detect and contain threats from hours or days to minutes or seconds. This rapid response capability minimizes potential damage and prevents lateral movement within your network."
    },
    {
      percentage: "50%",
      title: "Cost Efficiency", 
      description: "Lower total cost of ownership compared to traditional security solutions.",
      details: "By consolidating multiple security tools into a single platform and automating routine tasks, Spharaka XDR™ significantly reduces operational costs, licensing fees, and the need for specialized expertise. Organizations typically see a 50% reduction in total security spending within the first year of deployment."
    },
    {
      percentage: "70%",
      title: "Increased Productivity",
      description: "Free up your security team to focus on strategic initiatives rather than routine tasks.",
      details: "Security analysts spend less time on manual threat hunting, false positive investigation, and routine monitoring. Our platform's automation capabilities handle these tasks, allowing your team to focus on high-value activities like security architecture improvements, threat intelligence analysis, and strategic planning."
    },
    {
      percentage: "100%",
      title: "Seamless Scalability",
      description: "Scale your security operations effortlessly as your business grows.",
      details: "Whether you're adding new endpoints, expanding to the cloud, or integrating acquisitions, Spharaka XDR™ scales with your business. Our cloud-native architecture adapts to your growing environment without performance degradation or the need for complex reconfigurations."
    },
    {
      percentage: "90%",
      title: "Effortless Compliance",
      description: "Streamline compliance reporting and maintain regulatory requirements with ease.",
      details: "Built-in compliance frameworks and autonomous reporting capabilities simplify adherence to regulations like GDPR, HIPAA, PCI DSS, and SOC 2. Our platform continuously monitors compliance status and provides real-time visibility into control effectiveness, reducing audit preparation time by up to 90%."
    }
  ];

  const handleToggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section 
      className="py-12 md:py-20 relative overflow-hidden" 
      id="efficiency"
      style={{
        backgroundImage: `url(/lovable-uploads/a78cf8b7-1261-4354-a9e8-4ebcfec163f8.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/40"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-[600px]">
          {/* Left side - empty space to show background */}
          <div className="hidden lg:block"></div>
          
          {/* Right side - content */}
          <div className="space-y-6">
            <div className="text-center lg:text-left mb-8 md:mb-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-white">
                Maximize <span className="text-[#8932c8]">Cybersecurity Efficiency</span>
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-white/90 max-w-2xl lg:max-w-none">
                with AI-Driven Threat Detection and Response
              </p>
            </div>

            <div className="space-y-4">
              {metrics.map((metric, index) => (
                <div 
                  key={index} 
                  className={`metric-item animate-fade-in transition-all duration-300 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 cursor-pointer hover:bg-white/20 ${expandedIndex === index ? 'border-[#8932c8] bg-white/20' : ''}`}
                  style={{ animationDelay: `${index * 150}ms` }}
                  onClick={() => handleToggleExpand(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 md:gap-4">
                      <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">{metric.percentage}</span>
                      <span className="text-sm sm:text-base font-medium text-white">{metric.title}</span>
                    </div>
                    <button 
                      className={`${expandedIndex === index ? 'bg-[#8932c8] text-white' : 'text-[#8932c8] hover:bg-[#8932c8]/10'} p-1 rounded-full transition-colors`}
                      aria-label={expandedIndex === index ? `Collapse ${metric.title}` : `Expand ${metric.title}`}
                    >
                      {expandedIndex === index ? (
                        <Minus className="h-5 w-5" />
                      ) : (
                        <Plus className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  
                  {expandedIndex === index && (
                    <div className="mt-3 md:mt-4 pl-8 sm:pl-10 md:pl-14 pr-2 md:pr-4 pb-2 text-xs sm:text-sm md:text-base text-white/90 animate-fade-in leading-relaxed">
                      {metric.details}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EfficiencySection;
